import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';
import RegistrationStep1 from './registration/RegistrationStep1';
import RegistrationStep2 from './registration/RegistrationStep2';
import RegistrationStep3 from './registration/RegistrationStep3';
import RegistrationStep4 from './registration/RegistrationStep4';
import SuccessModal from './common/SuccessModel';
import RoomAllocationScreen from './Roomallocationscreen ';

/* ── Feature item — same as LoginScreen ── */
const FeatureItem = ({ text, delay }) => (
  <div className="feature-item" style={{ animationDelay: delay }}>
    <div className="feature-icon"></div>
    <span className="feature-text">{text}</span>
  </div>
);

/* ── Left panel — same as LoginScreen ── */
const LeftPanel = ({ subtitle, features }) => (
  <div className="login-left">
    <div className="login-branding">
      <div className="logo-lockup">
        <div className="logo-text">OMKARA</div>
        <div className="logo-divider"></div>
        <div className="logo-tagline">Hostel Management</div>
      </div>
      <p className="login-welcome">{subtitle}</p>
      <div className="login-features">
        {features.map((f, i) => <FeatureItem key={i} text={f} delay={`${0.2 + i * 0.2}s`} />)}
      </div>
    </div>
  </div>
);

/* ── Timer hook ── */
function useTimer(seconds = 60) {
  const [timer, setTimer] = useState(0);
  const start = () => setTimer(seconds);
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);
  return [timer, start];
}

/* ── OTP input row ── */
const OtpRow = ({ values, setValues, inputRefs }) => {
  const handleChange = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...values];
    next[i] = v;
    setValues(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !values[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };
  return (
    <div style={{ display: 'flex', gap: 10, margin: '16px 0' }}>
      {values.map((v, i) => (
        <input
          key={i}
          ref={el => inputRefs.current[i] = el}
          type="tel" maxLength={1} value={v}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          style={{
            width: 46, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 700,
            border: v ? '2px solid #1B3B5F' : '2px solid #E8E4DF',
            borderRadius: 8, outline: 'none', fontFamily: "'Work Sans', sans-serif",
            background: v ? '#F0F4F8' : 'white', color: '#1B3B5F',
            transition: 'border-color 0.2s',
          }}
        />
      ))}
    </div>
  );
};

/* ── Shared styles ── */
const S = {
  errBox:    { background: 'rgba(232,93,74,0.08)', borderLeft: '4px solid #E85D4A', padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#E85D4A', borderRadius: 4 },
  succBox:   { background: '#D4EDDA', borderLeft: '4px solid #2D5F4F', padding: '20px', borderRadius: 8, textAlign: 'center', marginBottom: 20 },
  inputWrap: { display: 'flex', alignItems: 'center', border: '2px solid #E8E4DF', borderRadius: 8, overflow: 'hidden' },
  inputPrefix: { padding: '14px', background: '#F8F6F3', color: '#6B6B6B', fontSize: 15, fontWeight: 600, borderRight: '1px solid #E8E4DF', whiteSpace: 'nowrap' },
  otpFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
  otpTimer:  { fontSize: 13, color: '#6B6B6B' },
  resendBtn: (disabled) => ({ fontSize: 13, fontWeight: 600, color: disabled ? '#C4BEB8' : '#1B3B5F', background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer' }),
  backLink:  { fontSize: 13, fontWeight: 600, color: '#6B6B6B', background: 'none', border: 'none', cursor: 'pointer' },
};

/**
 * GuestRegistration — Public page at /register
 * Shows OTP verification first, then the 4-step registration form.
 */
const GuestRegistration = () => {
  const [screen,      setScreen]      = useState('otp');
  const [navKey,      setNavKey]      = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  /* OTP state */
  const [mobile,   setMobile]   = useState('');
  const [otpStep,  setOtpStep]  = useState('mobile');
  const [otp,      setOtp]      = useState(['','','','','','']);
  const [timer,    startTimer]  = useTimer(60);
  const otpRefs = useRef([]);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const sendOTP = async () => {
    setError('');
    if (mobile.length !== 10) { setError('Please enter a valid 10-digit mobile number.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/send-otp', { mobile });
      setOtpStep('otp');
      setOtp(['','','','','','']);
      startTimer();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const verifyOTP = async () => {
    setError('');
    const code = otp.join('');
    if (code.length < 6) { setError('Please enter the complete 6-digit OTP.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/login-otp', { mobile, otp: code });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify({
          id:        res.data.id,
          username:  res.data.username,
          role:      res.data.role,
          firstName: res.data.firstName,
          lastName:  res.data.lastName,
        }));
        setOtpStep('success');
        localStorage.setItem('guestMobile', mobile);
        setTimeout(() => navigateTo('registration1'), 1200);
      } else {
        setError('Invalid or expired OTP. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const resetOtp = () => {
    setOtpStep('mobile'); setMobile('');
    setOtp(['','','','','','']); setError('');
  };

  const navigateTo = useCallback((sc, params = {}) => {
    if (sc === 'registration1' && !params.isBack) {
      localStorage.removeItem('registrationStep1Data');
      localStorage.removeItem('registrationStep2Data');
      localStorage.removeItem('registrationStep3Data');
      localStorage.removeItem('registrationPhotoPreview');
      window.selectedPhotoFile = null;
      window.selectedDocFiles  = null;
      sessionStorage.removeItem('regPhotoData');
    }
    setScreen(sc);
    setNavKey(k => k + 1);
    window.scrollTo(0, 0);
  }, []);

  const handleLogout = () => {
    setScreen('otp');
    resetOtp();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setScreen('rooms');
  };

  /* ── OTP Screen ── */
  if (screen === 'otp') {
    return (
      <div className="login-container">
        <LeftPanel
          subtitle="Start your admission journey. Verify your mobile number and complete the registration form — takes less than 10 minutes."
          features={[
            'Fill once — admin reviews and confirms',
            'Upload KYC documents digitally',
            'Track your application status in real time',
          ]}
        />

        <div className="login-right">
          <div className="login-card">


            <div className="login-header">
              <h2>Guest Registration</h2>
              <p>Verify your mobile number to begin</p>
            </div>

            {error && <div style={S.errBox}>⚠️ {error}</div>}

            {/* Success */}
            {otpStep === 'success' && (
              <div style={S.succBox}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✓</div>
                <div style={{ fontWeight: 700, color: '#155724', fontSize: 15 }}>
                  Mobile verified! Opening your registration form…
                </div>
              </div>
            )}

            {/* Step 1 — Mobile */}
            {otpStep === 'mobile' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Your Mobile Number</label>
                  <div style={S.inputWrap}>
                    <span style={S.inputPrefix}>+91</span>
                    <input
                      type="tel" className="form-input has-prefix"
                      placeholder="98765 43210" maxLength={10}
                      value={mobile}
                      onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={e => e.key === 'Enter' && sendOTP()}
                      style={{ border: 'none', outline: 'none', flex: 1 }}
                    />
                  </div>
                </div>
                <p style={{ fontSize: 13, color: '#6B6B6B', marginBottom: 0, lineHeight: 1.6 }}>
                  This number will be your login credential and the primary contact for all hostel communications.
                </p>
                <button className="btn-primary btn-gold" onClick={sendOTP} disabled={loading} style={{ marginTop: 28 }}>
                  {loading ? 'Sending OTP…' : 'Send OTP'}
                </button>
              </div>
            )}

            {/* Step 2 — OTP */}
            {otpStep === 'otp' && (
              <div>
                <div className="form-group">
                  <label className="form-label">OTP sent to +91 {mobile.slice(0,5)} {mobile.slice(5)}</label>
                  <OtpRow values={otp} setValues={setOtp} inputRefs={otpRefs} />
                  <div style={S.otpFooter}>
                    {timer > 0
                      ? <span style={S.otpTimer}>Resend in <strong style={{ color: '#1B3B5F' }}>{timer}s</strong></span>
                      : <span />}
                    <button style={S.resendBtn(timer > 0)} disabled={timer > 0} onClick={sendOTP}>Resend OTP</button>
                  </div>
                </div>
                <button className="btn-primary btn-gold" onClick={verifyOTP} disabled={loading}>
                  {loading ? 'Verifying…' : 'Verify & Open Registration Form'}
                </button>
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <button style={S.backLink} onClick={resetOtp}>← Change number</button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  /* ── Registration Steps 1–4 ── */
  return (
    <div className="app">
      {screen === 'registration1' && <RegistrationStep1 key={`s1-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} />}
      {screen === 'registration2' && <RegistrationStep2 key={`s2-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} />}
      {screen === 'registration3' && <RegistrationStep3 key={`s3-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} />}
      {screen === 'registration4' && <RegistrationStep4 key={`s4-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} onSubmit={() => setShowSuccess(true)} />}
      {screen === 'rooms'         && <RoomAllocationScreen onNavigate={navigateTo} onLogout={handleLogout} />}
      {showSuccess && <SuccessModal onClose={handleSuccessClose} />}
    </div>
  );
};

export default GuestRegistration;